import { useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  ActionButton,
  ActionButtonIconPlacement,
  ActionButtonIntent,
  Item,
  MainMenuOptionItem,
  MenuListBox,
  Popover,
  useContextualMenu,
} from "widgets";

import type { Configuration, User } from "@/graphql.ts";
import { filterByCapability } from "@/modules/users/utils/user.ts";
import { filterByLicense } from "@/redux/modules/config.ts";
import { downloadJnlpFile } from "@/redux/modules/files.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { GETTING_STARTED_ELEMENT_ID } from "@/settings/settings.ts";
import { visualizerData } from "@/ui-modules/core/navbar/settings.js";

export function AnalyzeMenu(): JSX.Element {
  const dispatch = useDispatch<ReduxDispatch>();
  const navigate = useNavigate();
  const session = useSelector<IAppState, User>((state) => state.auth.session);
  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration);
  const visualizers = useMemo(() => {
    return visualizerData.map((data) =>
      filterByCapability(filterByLicense(data, configuration), session?.capabilities),
    );
  }, [configuration, session]);

  const triggerRef = useRef(null);
  const { state, triggerProps, menuProps } = useContextualMenu(triggerRef);

  const allAnalyzeItems = visualizers.flatMap((groupItems) => {
    return groupItems.flat();
  });

  return (
    <>
      <ActionButton
        {...triggerProps}
        ref={triggerRef}
        label="Analyze"
        intent={state.isOpen ? ActionButtonIntent.Active : ActionButtonIntent.Secondary}
        icon="keyboard_arrow_down"
        iconPlacement={ActionButtonIconPlacement.Right}
        id={GETTING_STARTED_ELEMENT_ID.CONTENT_BROWSER}
      />
      <Popover state={state} triggerRef={triggerRef} placement="bottom start">
        <MenuListBox
          {...menuProps}
          onAction={(key) => {
            const item = visualizers.flat().find((item) => item.title === key);
            if (item.jnlpModuleId) {
              void dispatch(downloadJnlpFile({ moduleId: item.jnlpModuleId }));
            } else if (item.url) {
              navigate(item.url);
            }
          }}
        >
          {allAnalyzeItems.map((item) => (
            <Item key={item.title}>
              <MainMenuOptionItem
                id={item.title}
                icon={<img src={item.imgPath || item.img} width={30} height={30} />}
                title={item.title}
                description={item.description}
                tag={item.tag}
              />
            </Item>
          ))}
        </MenuListBox>
      </Popover>
    </>
  );
}
